import { ArrowLeftOutlined, InboxOutlined } from "@ant-design/icons"
import { Button, Card, Divider, Flex, Tooltip, Typography } from "antd"
import { type ItemType } from "antd/es/menu/interface"
import dayjs from "dayjs"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { RecordRef } from "../../../../../components/RecordRef.tsx"
import { getEntityName } from "../../../../../utils/user/get-entity-name"
import { leadDisplayName } from "../../../helpers/lead-display-name.ts"
import { LeadsStatusCell } from "../../../../Leads/components/LeadsStatusCell.tsx"
import { type LeadsFilterOptions } from "../../../../Leads/redux/leads.types.ts"
import { type Lead } from "../../../types/lead.types.ts"
import { LeadStageRetryBanner } from "../../missing/LeadStageRetryBanner.tsx"
import {
  useLeadAllMissingFields,
  useLeadMissingCardFields
} from "../../missing/lead-missing-fields-context.tsx"
import {
  LEAD_HEADER_APPOINTMENT_TARGET,
  LEAD_HEADER_OWNER_TARGET
} from "../../missing/lead-missing-fields.ts"
import { LeadWorkspaceOwner } from "./LeadWorkspaceOwner.tsx"
import { LeadWorkspaceQuickActions } from "./LeadWorkspaceQuickActions.tsx"
import { LeadWorkspaceSchedule } from "./LeadWorkspaceSchedule.tsx"
import { LeadWorkspaceStates } from "./LeadWorkspaceStates.tsx"
import { type LeadHeaderPatch } from "./lead-header-patch.ts"

const { Text, Title } = Typography

interface LeadWorkspaceHeaderProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  canAssignOwner?: boolean
  saving?: boolean
  pendingStatus?: number | null
  onLeadSelect: (id: string, action: string) => void
  onStageChange: (targetStatus: number) => void
  onStageRetry?: () => void
  onDismissPendingStage?: () => void
  onPatch: (patch: LeadHeaderPatch) => Promise<boolean>
}

export function LeadWorkspaceHeader({
  lead,
  filterOptions,
  canEdit,
  canAssignOwner,
  saving,
  pendingStatus = null,
  onLeadSelect,
  onStageChange,
  onStageRetry,
  onDismissPendingStage,
  onPatch
}: LeadWorkspaceHeaderProps) {
  const { t } = useTranslation()
  const missingFields = useLeadAllMissingFields()
  const ownerMissing = !!useLeadMissingCardFields(LEAD_HEADER_OWNER_TARGET)
    .length
  const appointmentMissing = !!useLeadMissingCardFields(
    LEAD_HEADER_APPOINTMENT_TARGET
  ).length

  const statuses = filterOptions?.status ?? []
  const owners = filterOptions?.sales_manager ?? []

  const statusMap = useMemo(
    () =>
      statuses.reduce<Record<string, unknown>>((acc, { label, value }) => {
        acc[label] = value
        return acc
      }, {}),
    [statuses]
  )

  const customer = lead?.customer || lead?.temporary_customer
  const customerName = getEntityName(customer, "") || null
  const title = leadDisplayName(lead, t("leadWorkspace.header.untitled"))
  const typeDisplay =
    lead?.project_type_details?.map((type) => type.name).join(", ") || null

  const canArchive = canEdit && !lead?.is_closed_lost && !lead?.is_project

  const moreItems = useMemo(() => {
    const items: ItemType[] = []

    if (canArchive) {
      items.push({
        key: "archive",
        icon: <InboxOutlined />,
        danger: true,
        label: t("leadArchive.action"),
        onClick: () => onLeadSelect(lead.id, "archive")
      })
    }

    return items
  }, [canArchive, lead?.id, onLeadSelect, t])

  return (
    <Card
      size={"small"}
      className={"rounded-2xl"}
      styles={{ body: { padding: "0 24px" } }}
      data-testid={"lead-workspace-header"}
    >
      <LeadStageRetryBanner
        targetStatus={pendingStatus}
        statuses={statuses}
        fields={missingFields}
        saving={saving}
        onRetry={() => onStageRetry?.()}
        onDismiss={() => onDismissPendingStage?.()}
      />

      {/* Level 1 — identity: who/what this is. Left-aligned, never centered. */}
      <Flex
        gap={"middle"}
        align={"start"}
        justify={"space-between"}
        className={"w-full"}
        style={{ paddingTop: 6, paddingBottom: 4 }}
      >
        <Flex gap={"middle"} align={"start"} className={"min-w-0"}>
          <Tooltip title={t("leadWorkspace.header.back")}>
            {/* Browser history, not a fixed destination: a lead opened from
                Archive returns to Archive, one opened from the active list
                returns there — `back_to_list` already encodes the fallback
                for a lead reached with no history (a bookmark, a new tab). */}
            <Button
              aria-label={t("leadWorkspace.header.back")}
              icon={<ArrowLeftOutlined />}
              size={"large"}
              onClick={() => onLeadSelect(lead.id, "back_to_list")}
            />
          </Tooltip>

          <Flex vertical gap={4} className={"min-w-0"}>
            <Title
              level={4}
              className={"m-0 min-w-0"}
              style={{ fontSize: 28, lineHeight: 1.25 }}
              ellipsis={{ tooltip: title }}
            >
              {title}
            </Title>
            <div style={{ fontSize: 14 }}>
              <RecordRef
                uid={lead?.uid}
                type={typeDisplay}
                city={lead?.city}
                state={lead?.state}
                customerName={customerName}
              />
            </div>
            {lead?.created && (
              <Text type={"secondary"} style={{ fontSize: 13 }}>
                {t("leadWorkspace.header.created", {
                  date: dayjs(lead.created).format("MMM D, YYYY")
                })}
              </Text>
            )}
          </Flex>
        </Flex>

        <div className={"shrink-0"}>
          <LeadWorkspaceQuickActions lead={lead} moreItems={moreItems} />
        </div>
      </Flex>

      <Divider className={"m-0"} />

      {/* Level 2 — controls: one compact row, grouped by a hairline rather
          than a container, and never stretched to fill a wide screen. */}
      <Flex
        gap={16}
        wrap
        align={"start"}
        style={{ paddingTop: 12, paddingBottom: 11 }}
      >
        {/* Process state: stage + operational state belong together. An
            invisible label the same height as "Owner" etc. lines its value
            up with the other columns' value line, not their whole block. */}
        <Flex vertical gap={2}>
          <Text className={"text-xs invisible"}>{" "}</Text>
          <Flex gap={8} wrap align={"center"}>
            <LeadsStatusCell
              statusDisplay={lead?.status_name}
              statusMap={statusMap}
              statuses={statuses}
              isProject={lead?.is_project || false}
              isClosedLost={lead?.is_closed_lost}
              editable={canEdit}
              onSelect={onStageChange}
              onArchive={() => onLeadSelect(lead.id, "archive")}
            />

            <LeadWorkspaceStates
              states={lead?.operational_states}
              needsInfoItems={lead?.needs_info_items}
              owners={owners}
              canEdit={canEdit}
              saving={saving}
              onChange={onPatch}
            />
          </Flex>
        </Flex>

        <div className={"hidden w-px self-stretch bg-gray-200 sm:block"} />

        <LeadWorkspaceOwner
          salesManagers={lead?.sales_manager}
          owners={owners}
          canAssign={canAssignOwner}
          saving={saving}
          missing={ownerMissing}
          onChange={onPatch}
        />

        <div className={"hidden w-px self-stretch bg-gray-200 sm:block"} />

        <LeadWorkspaceSchedule
          lead={lead}
          owners={owners}
          canEdit={canEdit}
          saving={saving}
          appointmentMissing={appointmentMissing}
          onChange={onPatch}
        />
      </Flex>
    </Card>
  )
}
